/* eslint-disable max-lines */
import { PaymentMethod } from '@adyen/adyen-web/dist/types/types';

import {
  UrlType,
  Currency,
  BlockType,
  CustomerType,
  GiftCardVariationType,
  ProductType,
  OfferType,
  CountryCode,
  Language,
  VideoProvider,
} from './apiEnums';

export enum ProductListItemType {
  DonationProduct = 'DonationProduct',
  GiftcardProduct = 'GiftcardProduct',
  TopProduct = 'TopProduct',
}

export type ValueChangedEvent = {
  target: {
    value: string;
  };
};

export type PreventDefaultEvent = {
  preventDefault: () => void;
};
export type PreventDefaultEventWithReturnValue = {
  preventDefault: () => void;
  returnValue: string;
};

export enum ShipmentTag {
  Home = 'home',
  HomeEvoucher = 'home-evoucher',
}

export enum AdjustmentType {
  Shipping = 'shipping',
}

export enum DeliveryMethodType {
  ByMail = 'byMail',
  Digital = 'digital',
}

export enum RedeemGiftCardModalProductTypes {
  DonationProduct = 'DonationProduct',
  GiftcardProduct = 'GiftcardProduct',
}

export enum RedeemGuestModalType {
  Digital = 'Digital',
  Physical = 'Physical',
}

export type Message = {
  defaultMessage: string;
  id: string;
};

export type ColorPresentational = {
  color: string;
  opacity: number;
};

export type ImagePresentational = {
  alt: string | null;
  url: UrlPresentational;
};

export type ImageWithHandPresentational = ImagePresentational & {
  showHand?: boolean;
};

export type UrlPresentational = {
  href: string;
  type: UrlType;
};

export type PricePresentational = {
  currency: Currency;
  value: number;
};

export type LinkPresentational = {
  title: string;
  url: UrlPresentational;
};

export type BlockPresentational = {
  blockType: BlockType;
};

export type TitleAndBodyBlockPresentational = {
  body: string;
  title: string;
};

export type TitleBodyImageBlockPresentational = {
  image: ImagePresentational;
} & TitleAndBodyBlockPresentational;

export type TitleBodyImageLinkBlockAllOptionalPresentational = {
  body?: string | null;
  image?: ImagePresentational | null;
  link?: LinkPresentational | null;
  title?: string | null;
};

export type FullWidthBlocksPresentational = BlockPresentational & (
  | TitleAndBodyBlockPresentational
  | TitleBodyImageBlockPresentational
  | TitleBodyImageLinkBlockPresentational
);

export type BankPresentational = {
  id: string;
  issuer?: string;
  logo: ImagePresentational | null;
  name: string | null;
};

export type BankMapped = BankPresentational & {
  isActive: boolean;
  onSelect: () => void;
};

export type PaymentMethodPresentational = {
  adyenRawData?: PaymentMethod;
  banks?: BankPresentational[];
  collectBillingInfo: boolean;
  description: string | null;
  gatewayId: string;
  id: string;
  logo: ImagePresentational | null;
  logoGallery?: ImagePresentational[] | null;
  title: string;
};

export type PaymentMethodMapped = PaymentMethodPresentational & {
  isActive: boolean;
  onPaymentMethodChange: () => void;
};

export type ErrorPresentational = {
  code?: number;
  message: string;
  referencedRequestParameter?: string | null;
};

export type TitleBodyImageLinkBlockPresentational = {
  body: string;
  image: ImagePresentational;
  link: LinkPresentational;
  title: string;
};

export type TitleBodyLinkImagePositionBlockPresentational =
TitleBodyImageLinkBlockPresentational & {
  imageOnLeft: boolean;
};

export type BodyImageTitleSubtitleLinkBlockPresentational = {
  body: string;
  image: ImagePresentational | null;
  imageSubTitle: string | null;
  imageTitle: string | null;
  link: LinkPresentational | null;
};

export type TitleSubtitleImageBlockPresentational = {
  image: ImagePresentational;
  subtitle: string;
  title: string;
};

export type BigBottomBlockPresentational = {
  backgroundColor: ColorPresentational;
  brands: ImagePresentational[];
  button: LinkPresentational | null;
  image: ImagePresentational;
  showHand: boolean;
  stars: number | null;
  title: string;
  usps: string[];
};

export type EntityReferenceBlockWithHandPresentational = {
  backgroundImage: ImagePresentational;
  cardImage: ImagePresentational;
  description: string;
  referencedPath: string | null;
  showHand: boolean;
  showShadow?: boolean;
  title: string;
};

export type ProductVariationPresentational = {
  id: string;
  price: PricePresentational;
  sku?: string;
  title: string;
};

export type LocationOverviewLinkPresentational = {
  id: number;
  link: LinkPresentational;
  title: string;
};

export type NamePresentational = {
  firstName: string | null;
  lastName: string | null;
};

export type AddressPreviewPresentational = {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  houseNumber: string | null;
  postCode: string | null;
  street: string | null;
  suffix: string | null;
};

export type AddressAsText = {
  postCodeAndCity: string;
  streetAndHouseNumberAndSuffix: string;
};

export type AddressPresentational = NamePresentational & AddressPreviewPresentational & {
  companyName: string | null;
  countryCode: string | null;
  customerType: CustomerType | null;
  email: string | null;
  invoiceEmail: string | null;
  phoneNumber: string | null;
  referenceNumber: string | null;
  vatNumber: string | null;
};

export type ImageBodyBlockPresentational = BlockPresentational & {
  body: string;
  image: ImagePresentational;
};

export type GiftcardProductPresentational = {
  backgroundColor: ColorPresentational;
  brands: ImagePresentational[];
  breadcrumbs: LinkPresentational[] | null;
  description: string | null;
  digitalWrappers: DigitalWrapperPresentational[];
  fixedValues: boolean;
  gallery?: ImagePresentational[] | ImageWithHandPresentational[] | null;
  hasDigitalWrappers: boolean;
  hasGiftPackings: boolean;
  hasWrappers: boolean;
  highlightedWrapperCategory: WrapperCategoryPresentational | null;
  id: string;
  image: ImagePresentational | ImageWithHandPresentational;
  locationOverviewLink: LocationOverviewLinkPresentational | null;
  outOfStock: boolean;
  path: string | null;
  productType: ProductType;
  promotionGiftcard: ImageBodyBlockPresentational | null;
  seoBlocks: TitleAndBodyBlockPresentational[];
  showHand: boolean;
  showPromotedGiftcard: boolean | null;
  similarGiftcards: DatalayerProductCardProps<null>[] | null;
  title: string;
  usps: string[];
  variations: GiftcardProductVariationPresentational[];
  variationsToShow: GiftcardProductVariationPresentational[];
  wrapperCategories: WrapperCategoryPresentational[];
  wrappers: (PackageItemsListWrapper | DigitalWrapperPresentational)[];
};

export type PackageItemsListWrapper = {
  backgroundColor?: ColorPresentational;
  gallery?: ImagePresentational[];
  id: string;
  image: ImagePresentational;
  showGiftPackings: boolean;
  title: string;
  usps?: string[];
  variations: WrapperProductVariationPresentational[];
};

export type ProductListItemPresentational = {
  backgroundColor: ColorPresentational | null;
  backgroundImage: ImagePresentational | null;
  cardImage: ImagePresentational | null;
  description: string | null;
  id: string | null;
  image: ImagePresentational | null;
  path: string | null;
  productType?: ProductType;
  showHand: boolean;
  showShadow?: boolean;
  title: string;
  type: ProductListItemType;
};

export type PackageItemPricePresentational = {
  price?: PricePresentational;
  qty?: number;
  title?: string;
};

export type OriginalPackagePricePresentational = {
  packageItemPrice: PackageItemPricePresentational[];
  totalExclusivePrice: PricePresentational;
  totalInclusivePrice: PricePresentational;
};

export type GiftcardProductVariationPresentational = {
  id: string;
  price: PricePresentational;
  product: {
    id: string;
    title: string;
  };
  productType?: ProductType;
  sku: string | null;
  title?: string;
  variationType?: GiftCardVariationType;
};

export type WrapperProductVariationPresentational = {
  id: string;
  price: PricePresentational;
  sku: string;
  title: string;
};

export type GiftPackingProductVariationPresentational = {
  id: string;
  price: PricePresentational;
  sku: string;
  title: string;
};

export type DeliveryMethodPresentational = {
  id: DeliveryMethodType;
  isAlternativeChoice?: boolean;
  variations: GiftcardProductVariationPresentational[];
  variationsToShow: GiftcardProductVariationPresentational[];
};

export enum ProductCardType {
  L = 'large',
  S = 'small',
}

export enum ProductCardLabelColor {
  primary = 'primary',
  secondary = 'secondary',
}

export enum ProductCardLabelIcon {
  like = 'like',
  star = 'star',
}

export type ProductCardLabel = {
  color: ProductCardLabelColor;
  icon?: ProductCardLabelIcon;
  title: JSX.Element;
};

export type ProductCardProps = {
  backgroundColor?: ColorPresentational | null;
  backgroundImage?: ImagePresentational | null;
  cardImage: ImagePresentational | null;
  cardType: ProductCardType;
  className?: string;
  description?: string | null;
  disablePath?: boolean;
  isExsternalLink?: boolean;
  isReorder?: boolean;
  label?: ProductCardLabel;
  onClick?: (id: string | null) => void;
  path?: string | null;
  productType?: ProductType;
  showFreeLabel?: boolean;
  showHand: boolean;
  showShadow?: boolean;
  title: string;
};

export type DatalayerProductCardProps<TProduct = null,> = ProductCardProps & {
  id: string | null;
  isRedeem?: boolean;
  position: number;
  product?: TProduct | null;
};

export type PageRoutingParams = {
  page: string | undefined;
};

export type URLWithPageRoutingParams = PageRoutingParams & {
  url: string;
};

export type PackageGroupCardPreviewPresentational = {
  color: ColorPresentational;
  image: ImagePresentational;
  productLabel: string;
};

export type GiftcardPackageGroupPresentational = {
  cardsPreviews: PackageGroupCardPreviewPresentational;
  packageId: string;
  receiverDescription: string;
  receiverTitle: string;
  tag: string;
  totalPrice: PricePresentational;
};

export type WrapperProductPresentational = {
  backgroundColor: ColorPresentational;
  gallery: ImagePresentational[];
  id: string;
  image: ImagePresentational;
  productType: ProductType;
  showGiftPackings: boolean;
  title: string;
  usps: string[];
  variations: WrapperProductVariationPresentational[];
};

export type GiftPackingProductPresentational = {
  backgroundColor: ColorPresentational;
  id: string;
  image: ImagePresentational;
  productType: ProductType;
  variations: GiftPackingProductVariationPresentational[];
};

export type DigitalWrapperPresentational = {
  backgroundColor: ColorPresentational;
  id: string;
  image: ImagePresentational;
  productType: ProductType;
  title: string;
  variations: GiftPackingProductVariationPresentational[];
};

export type ShippingMethodPresentational = {
  description: string | null;
  id: string;
  price: PricePresentational;
  title: string;
};

export type ValueIncreaseInPercentOfferPresentational = {
  type: OfferType;
  value: number;
};

export type PromotionPresentational = {
  description: string | null;
  offer: ValueIncreaseInPercentOfferPresentational;
  title: string;
};

export type RedeemDataPresentational = {
  description: string | null;
  maximumAvailableCustomAmount: PricePresentational | null;
  minimumAvailableCustomAmount: PricePresentational | null;
  title: string;
};
export type RedeemDataListPresentational = RedeemDataPresentational & {
  availableAsPhysical: boolean;
  availableAsVoucher: boolean;
  digitalShippingCost?: PricePresentational;
  digitalWrappers: DigitalWrapperPresentational[];
  extraDescription: string | null;
  hasDigitalWrappers: boolean;
  hasWrappers: boolean;
  minimumRemainingBalance: PricePresentational;
  organizationLink?: LinkPresentational | null;
  physicalShippingCost?: PricePresentational;
  promotion: PromotionPresentational | null;
  showHand: boolean;
  usps: string[];
  wrappers: WrapperProductPresentational[];
};
export type RedeemProductListItemPresentational = ProductListItemPresentational & {
  fixedValues: boolean;
  redeemData?: RedeemDataListPresentational | null;
  type: ProductListItemType;
  typename?: 'DonationProduct' | 'GiftcardProduct';
  variationsToShow: GiftcardProductVariationPresentational[];
};
export type VariationConfigPresentational = {
  hasFixedPrices: boolean;
  maxPrice: PricePresentational | null;
  minPrice: PricePresentational | null;
  pricesToShow: PricePresentational[];
  type: GiftCardVariationType;
};
export type RedeemSelectedProduct = {
  backgroundColor: ColorPresentational | null;
  digitalVariationConfig?: VariationConfigPresentational | null;
  digitalWrappers: DigitalWrapperPresentational[];
  fixedValues: boolean;
  hasDigitalWrappers: boolean;
  hasGiftPackings?: boolean;
  highlightedWrapperCategory: WrapperCategoryPresentational | null;
  id: string;
  image: ImagePresentational | null;
  outOfStock?: boolean;
  physicalVariationConfig?: VariationConfigPresentational | null;
  redeemData?: RedeemDataListPresentational | null;
  title: string;
  typename?: 'DonationProduct' | 'GiftcardProduct' | 'ProductReferenceBlock';
  variationsToShow: GiftcardProductVariationPresentational[];
  wrapperCategories: WrapperCategoryPresentational[];
  wrappers: PackageItemsListWrapper[];
};
export enum RedeemGiftCardModalProductTypesPresentational {
  DonationProduct = 'DonationProduct',
  GiftcardProduct = 'GiftcardProduct',
}
export type RedeemProductPriceRestriction = {
  maximumAvailableCustomAmount: PricePresentational | null;
  minimumAvailableCustomAmount: PricePresentational | null;
  minimumRemainingBalance: PricePresentational | null;
};
export type ProductQueryParams = {
  dv: string;
  type: string;
};
export type PersonalNoteSignal = {
  personalNote: string;
  shouldAddPersonalNote: boolean;
};
export type ProfilePresentational = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  companyName: string;
  confirmEmail: string;
  customerType: CustomerType;
  email: string;
  firstName: string;
  houseNumber: string;
  lastName: string;
  personalNote: string;
  postCode: string;
  shouldAddPersonalNote: boolean;
  street: string;
  suffix: string;
  vatNumber: string;
};
export type EditReceiverConfirmSignal = {
  packageIdToGo?: string;
  profile: ProfilePresentational;
};
export type LanguageWithMessages = {
  [key: string]: {[key: string]: string };
};
export type IpLocation = {
  city: string;
  continent_code: string;
  continent_name: string;
  country_code: CountryCode;
  country_name: string;
  ip: string;
  latitude: string;
  location: {
    calling_code: string;
    capital: string;
    country_flag: string;
    country_flag_emoji: string;
    country_flag_emoji_unicode: string;
    geoname_id: number;
    is_eu: true;
    languages: [{
      code: string;
      name: string;
      native: string;
    }];
  };
  longitude: string;
  region_code: string;
  region_name: string;
  type: string;
  zip: string;
};
export type WrapperCategoryPresentational = {
  id: string;
  title: string;
  wrappers: string[];
};

export type TestimonialBlockPresentational = {
  body: string;
  company: string;
  image: ImagePresentational;
  name: string;
  rating: number;
};

export type DropDownMenuPresentational = {
  className?: string;
  menuItems: MenuItemPresentational[];
  title: string;
};

export type MenuItemPresentational = {
  path: UrlPresentational | null;
  title: string;
};

export type EmbedVideoPresentational = {
  provider: VideoProvider;
  typename: 'EmbedVideo';
  url: UrlPresentational;
};

export type SelfHostedVideoPresentational = {
  mimeType: string;
  typename: 'SelfHostedVideo';
  url: UrlPresentational;
};

export type VideoPresentational = EmbedVideoPresentational | SelfHostedVideoPresentational;

export type AdjustmentPresentational = {
  adjustmentType: string;
  price: PricePresentational;
  title: string;
};

export type RedeemVariationOnFly = {
  price: PricePresentational;
};

export type ClientLanguageLink = {
  countryCode: CountryCode;
  langCode: Language;
  url: string;
};
